/**
 * Wohin ans **Ende** eines Abschnitts — die `order`-Werte für Aufgaben, die
 * hinten angehängt werden, mehrere auf einmal.
 *
 * Gebraucht vom **Übernehmen** (`.claude/tasks/canvas-notes-become-tasks.md`),
 * von allem, was eine Aufgabe in ein Projekt legt, das gerade keiner zeigt
 * (`features/taskManagement/utils/appendTaskOrders.ts`), und vom zweiten
 * Todoist-Import (#317). Darum in `lib/`: Der Import darf nicht aus `features/`
 * holen.
 *
 * Bewusst ohne React-Bezug und ohne Datenbankzugriff: Die Rechnung ist reine
 * Arithmetik über zwei Listen und soll ohne gerenderte Seite prüfbar sein.
 *
 * Dazu die Plätze für eine neue Aufgabe (`orderAtEndOf`, `orderAtTopOfQuickNotes`) — die lesen über eine
 * schmale Quelle und teilt die Mobile-App (Etappe 3).
 */

import type { LifeSpace, Section, Task } from './models'

export interface AppendOrderInput {
  /** Alle nicht gelöschten Aufgaben des Projekts. */
  tasks: readonly Pick<Task, 'order'>[]
  /** Alle nicht gelöschten Abschnitte des Projekts. */
  sections: readonly Pick<Section, 'id' | 'order'>[]
  /** Der Zielabschnitt, oder `null` für den abschnittslosen Block oben. */
  sectionId: string | null
  count: number
}

type Item = { order: number; sectionId: string | null }

/**
 * Aufgaben und Abschnitts-Köpfe in EINER nach `order` sortierten Folge, jeder
 * Eintrag mit dem Abschnitt, in dem er liegt.
 *
 * Der Abschnitt einer Aufgabe wird dabei aus der FOLGE gelesen und nicht aus
 * ihrem `sectionId`-Feld: Genau so baut die Seite ihre Segmente auf, und eine
 * zweite Lesart ergäbe hier eine andere Liste als dort zu sehen ist.
 */
function walk(tasks: AppendOrderInput['tasks'], sections: AppendOrderInput['sections']): Item[] {
  const raw = [
    ...tasks.map(t => ({ order: t.order, section: undefined as string | undefined })),
    ...sections.map(s => ({ order: s.order, section: s.id })),
  ].sort((a, b) => a.order - b.order)

  let current: string | null = null
  return raw.map(entry => {
    if (entry.section !== undefined) current = entry.section
    return { order: entry.order, sectionId: current }
  })
}

/**
 * Die `order`-Werte für `count` neue Aufgaben am Ende des Abschnitts.
 *
 * **Ohne Umnummerierung.** Steht hinter dem Abschnitt noch etwas, werden die
 * neuen Werte gleichmäßig in die Lücke davor gelegt statt alles dahinter
 * durchzuzählen. Bruchzahlen sind in dieser Liste ohnehin das etablierte
 * Mittel (`useTaskCreation` legt mit `+ 0.5` dazwischen), und eine
 * Umnummerierung schriebe für ein Anhängen ein Dutzend fremder Zeilen um.
 *
 * Ist der Abschnitt leer, zählt sein eigener Kopf als Ausgangspunkt; gibt es
 * ihn gar nicht, geht es ans Ende von allem. Ist der Block ohne Abschnitt leer,
 * geht es vor den ersten Abschnitt — dorthin, wo die Seite ihn zeigt.
 */
export function computeAppendOrders({ tasks, sections, sectionId, count }: AppendOrderInput): number[] {
  if (count <= 0) return []

  const items = walk(tasks, sections)
  const lastIndex = items.findLastIndex(item => item.sectionId === sectionId)

  // Vor dem ersten Abschnitt steht nichts: Ans Ende von allem hieße in den letzten Abschnitt (#317).
  if (lastIndex === -1 && sectionId === null && items.length > 0) {
    const first = items[0]!.order
    return Array.from({ length: count }, (_, i) => first - 1 + (i + 1) / (count + 1))
  }

  // Den Abschnitt gibt es (noch) nicht — oder die Liste ist leer. Dann ans
  // Ende von allem, und bei einer leeren Liste schlicht ab 0.
  if (lastIndex === -1) {
    const base = items.length > 0 ? Math.max(...items.map(i => i.order)) : -1
    return Array.from({ length: count }, (_, i) => base + 1 + i)
  }

  const base = items[lastIndex]!.order
  const next = items[lastIndex + 1]?.order

  // Nichts dahinter: weiterzählen.
  if (next === undefined) {
    return Array.from({ length: count }, (_, i) => base + 1 + i)
  }

  /*
    Gleichstand mit dem nächsten Kopf (Daten von vor #276: die erste Aufgabe eines Abschnitts trägt
    dessen Nummer). Weiterzählen landete hinter dem Kopf, im nächsten Abschnitt — darum in die Lücke
    VOR dem Gleichstand, hinter den letzten kleineren Wert. Die neuen stehen dann vor den gleich
    nummerierten, aber im richtigen Abschnitt (Diff-Audit #317).
  */
  if (next <= base) {
    const lower = items.slice(0, lastIndex).findLast(item => item.order < base)?.order ?? base - 1
    const step = (base - lower) / (count + 1)
    return Array.from({ length: count }, (_, i) => lower + step * (i + 1))
  }

  const step = (next - base) / (count + 1)
  return Array.from({ length: count }, (_, i) => base + step * (i + 1))
}

/**
 * Die `order` für EINE neue Aufgabe am Ende eines Abschnitts in einem Projekt, das gerade keiner zeigt
 * — etwa nach `#Steuer /Belege` im Titel (#234). Ohne Abschnitt: das Ende des Blocks ohne Abschnitt.
 */
export async function orderAtEndOf(
  source: { getTasksByProject(id: string): Promise<Task[]>; getSectionsByProject(id: string): Promise<Section[]> },
  projectId: string,
  sectionId: string | null,
): Promise<number> {
  const [tasks, sections] = await Promise.all([source.getTasksByProject(projectId), source.getSectionsByProject(projectId)])
  return computeAppendOrders({ tasks, sections, sectionId, count: 1 })[0]!
}

/**
 * Die `order` für eine **Kopie, die außerhalb der Projektseite entsteht** (⌘D in Ruhend, Wiederkehrend,
 * Demnächst): ans Ende ihres eigenen Abschnitts. Ohne Vorgabe landete sie hinter der größten Nummer —
 * am Ende des Projekts, unter dem letzten Abschnitt, obwohl ihr `sectionId` einen anderen nennt
 * (Diff-Audit #276). Ohne Projekt keine Vorgabe; dann vergibt die Datenbank den Platz.
 */
export async function orderAtEndOfOwnPlace(
  source: { getTasksByProject(id: string): Promise<Task[]>; getSectionsByProject(id: string): Promise<Section[]> },
  task: Pick<Task, 'projectId' | 'sectionId'>,
): Promise<number | undefined> {
  return task.projectId ? orderAtEndOf(source, task.projectId, task.sectionId ?? null) : undefined
}

/**
 * Die `order` für eine neue Notiz **ganz oben in Kurz notiert** (#247): unter der kleinsten der Notizen des
 * Bereichs — die neueste steht zuerst. Ohne Notizen keine Vorgabe; dann vergibt die Datenbank den Platz.
 */
export async function orderAtTopOfQuickNotes(
  source: { getQuickNotes(lifeSpace: LifeSpace): Promise<Task[]> },
  lifeSpace: LifeSpace,
): Promise<number | undefined> {
  const notes = await source.getQuickNotes(lifeSpace)
  return notes.length ? Math.min(...notes.map(note => note.order)) - 1 : undefined
}
